import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { ComposedChart, Bar, Line, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { apiGet, apiPost } from "../api";
import OverchargeTrendsChart from "../components/OverchargeTrendsChart";
import SupplierOverchargeModal from "../components/SupplierOverchargeModal";
import usePageTitle from "../hooks/usePageTitle";
import { useAuth } from "../auth/AuthContext";
import { formatCurrency, formatNumber } from "../lib/format";
import { Card, PageHeader, KpiRow, SectionHeading, Badge, StatusBadge, DataTable } from "../components/dc";

const pct = (a, b) => (b > 0 ? Math.min(Math.round((a / b) * 100), 100) : 0);

const monthShort = (ym) => {
  try { const [y, m] = String(ym).split("-"); return new Date(Number(y), Number(m) - 1, 1).toLocaleString("en-GB", { month: "short" }) + " " + y.slice(2); }
  catch { return ym; }
};
const gbpK = (v) => {
  const a = Math.abs(v || 0);
  if (a >= 1e6) return "£" + (v / 1e6).toFixed(1) + "M";
  if (a >= 1e3) return "£" + Math.round(v / 1e3) + "k";
  return "£" + Math.round(v || 0);
};

// Time-of-day greeting: morning < 12:00, afternoon 12:00–16:59, evening ≥ 17:00.
const greetingPart = () => {
  const h = new Date().getHours();
  return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
};

// Prefer the signed-in user's first name; fall back to the email local-part.
const firstNameOf = (user) => {
  if (user?.name?.trim()) return user.name.trim().split(/\s+/)[0];
  if (user?.email) return user.email.split("@")[0];
  return "there";
};

/* Ranked alert row (Top Overcharges / Likely Data Issues) */
const AlertRow = ({ rank, title, sub, value, valueColor, rankTone, onClick }) => (
  <div
    onClick={onClick}
    role={onClick ? "button" : undefined}
    tabIndex={onClick ? 0 : undefined}
    onKeyDown={onClick ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } } : undefined}
    style={{ display: "flex", alignItems: "center", gap: 14, padding: "12px 0", borderTop: "1px solid rgba(255,255,255,0.06)", cursor: onClick ? "pointer" : "default" }}
  >
    <div style={{ width: 26, height: 26, borderRadius: "50%", background: rankTone.bg, color: rankTone.color, fontSize: 12.5, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{rank}</div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 14.5, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{title}</div>
      <div style={{ fontSize: 12.5, color: "var(--color-text-3)", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{sub}</div>
    </div>
    <div style={{ fontFamily: "var(--font-display)", fontSize: 16, fontWeight: 700, color: valueColor, whiteSpace: "nowrap" }}>{value}</div>
  </div>
);

/* One "From across ProcOps" cross-link card */
const CrossLink = ({ to, tab, headline, sub, color }) => (
  <Link to={to} style={{ textDecoration: "none", display: "block" }}>
    <Card style={{ padding: "18px", borderRadius: 14 }}>
      <div style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--color-text-4)", marginBottom: 8 }}>{tab}</div>
      <div style={{ fontFamily: "var(--font-display)", fontSize: 19, fontWeight: 700, color }}>{headline}</div>
      <div style={{ fontSize: 12.5, color: "var(--color-text-3)", marginTop: 6 }}>{sub}</div>
    </Card>
  </Link>
);

const Dashboard = () => {
  usePageTitle("Dashboard");
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [metrics, setMetrics] = useState([]);
  const [metricsByMonth, setMetricsByMonth] = useState([]);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [selectedMonths, setSelectedMonths] = useState(() => new Set());
  const [over, setOver] = useState(null);
  const [cross, setCross] = useState(null);
  const [crossLoading, setCrossLoading] = useState(true);
  const [threshold, setThreshold] = useState(0);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [reductions, setReductions] = useState(null);
  const [freshness, setFreshness] = useState(null);
  const [impact, setImpact] = useState(null);

  useEffect(() => {
    apiGet("/settings").then((d) => {
      const t = Number(d?.overcharge_threshold_pct ?? 0);
      if (Number.isFinite(t) && t > 0) setThreshold(t);
    }).catch(() => {});
  }, []);

  const loadStats = useCallback(async () => {
    try {
      const d = await apiGet("/dashboard/stats", { timeout: 20000 });
      if (d) setStats({
        totalInvoices: d.total_invoices,
        totalPriceFiles: d.total_price_files,
        suppliersCovered: d.suppliers_covered || 0,
        suppliersWithInvoices: d.suppliers_with_invoices || 0,
        totalPotentialCredit: d.total_potential_credit,
        // Credit-recovery overlay (from credits_overlay). identified = gross.
        overchargeIdentified: d.overcharge_identified ?? d.total_potential_credit,
        creditReceived: d.credit_received || 0,
        creditExpected: d.credit_expected || 0,
        netOutstanding: d.net_outstanding ?? d.total_potential_credit,
        overRecovered: !!d.over_recovered,
        dataIssueCredit: d.data_issue_credit || 0,
        dataIssueCount: d.data_issue_count || 0,
      });
    } catch { /* leave null */ }
  }, []);

  const loadMetrics = useCallback(async () => {
    try {
      const d = await apiGet("/dashboard/supplier-metrics", { timeout: 30000 });
      setMetrics(Array.isArray(d?.metrics) ? d.metrics : []);
      setMetricsByMonth(Array.isArray(d?.metrics_by_month) ? d.metrics_by_month : []);
      setAvailableMonths(Array.isArray(d?.available_months) ? d.available_months : []);
    } catch { setMetrics([]); }
  }, []);

  const loadOver = useCallback(async () => {
    try {
      const d = await apiGet("/dashboard/top-overcharges", { timeout: 30000 });
      setOver({
        top_overcharges: Array.isArray(d?.top_overcharges) ? d.top_overcharges : [],
        grand_total: typeof d?.grand_total === "number" ? d.grand_total : 0,
        data_issues: Array.isArray(d?.data_issues) ? d.data_issues : [],
        data_issue_total: typeof d?.data_issue_total === "number" ? d.data_issue_total : 0,
        data_issue_count: typeof d?.data_issue_count === "number" ? d.data_issue_count : 0,
      });
    } catch { setOver(null); }
  }, []);

  const loadCross = useCallback(async () => {
    // Generous timeout: on a cold cache the endpoint computes inflation/benchmark
    // once (then it's cached), so allow for that rather than falling back to "—".
    try { setCross(await apiGet("/dashboard/cross-summary", { timeout: 45000 })); }
    catch { setCross(null); }
    finally { setCrossLoading(false); }
  }, []);

  const loadReductions = useCallback(async () => {
    try { setReductions(await apiGet("/dashboard/price-reductions", { timeout: 30000 })); }
    catch { setReductions({ reductions: [] }); }
  }, []);

  const loadFreshness = useCallback(async () => {
    try { setFreshness(await apiGet("/price-files/freshness", { timeout: 20000 })); }
    catch { setFreshness({ suppliers: [] }); }
  }, []);

  const loadImpact = useCallback(async () => {
    try { setImpact(await apiGet("/qbr/price-impact-timeline", { timeout: 30000 })); }
    catch { setImpact({ loaded: false, series: [], submissions: [] }); }
  }, []);

  const exportReductions = useCallback(() => {
    const rows = reductions?.reductions || [];
    if (!rows.length) return;
    const esc = (v) => {
      let s = v === null || v === undefined ? "" : String(v);
      if (typeof v === "string" && (/^[\s\x00-\x1f\x7f\ufeff]*[=+@-]/.test(s) || /^[\t\r\n]/.test(s))) s = "'" + s;
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const header = ["Supplier", "SKU", "Description", "Price file price", "Invoiced price",
      "% below", "Invoices", "Months", "Confidence", "Est. annual value"];
    const body = rows.map((r) => [
      r.supplier, r.sku, r.description, r.priceFilePrice, r.observedPrice,
      r.pctBelow, r.invoices, r.months, r.band, r.estAnnualValue,
    ].map(esc).join(","));
    const blob = new Blob([header.join(",") + "\n" + body.join("\n") + "\n"], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `uncaptured_price_reductions_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  }, [reductions]);

  const dismissReduction = useCallback(async (supplier, sku) => {
    try {
      await apiPost(`/dashboard/price-reductions/dismiss?supplier=${encodeURIComponent(supplier)}&sku=${encodeURIComponent(sku || "")}`);
      setReductions((prev) => prev ? { ...prev, reductions: prev.reductions.filter((r) => !(r.supplier === supplier && r.sku === sku)) } : prev);
    } catch { /* leave row in place on failure */ }
  }, []);

  useEffect(() => {
    Promise.all([loadStats(), loadMetrics(), loadOver(), loadCross(), loadReductions(), loadFreshness(), loadImpact()]);
  }, [loadStats, loadMetrics, loadOver, loadCross, loadReductions, loadFreshness, loadImpact]);

  const creditValue = stats?.totalPotentialCredit ?? over?.grand_total ?? 0;
  const identifiedValue = stats?.overchargeIdentified ?? creditValue;
  const recoveredValue = stats?.creditReceived ?? 0;
  const expectedValue = stats?.creditExpected ?? 0;
  const netValue = stats?.netOutstanding ?? creditValue;
  const hasCredits = recoveredValue > 0 || expectedValue > 0;
  const diValue = stats?.dataIssueCredit || over?.data_issue_total || 0;
  const diCount = stats?.dataIssueCount || over?.data_issue_count || 0;

  const kpis = [
    { label: "Total Invoices", value: stats ? formatNumber(stats.totalInvoices) : "—" },
    { label: "Price Files", value: stats ? formatNumber(stats.totalPriceFiles) : "—" },
    { label: "Suppliers Invoiced", value: stats ? formatNumber(stats.suppliersWithInvoices) : "—" },
  ];

  // Cross-link cards. Data card comes from stats; the rest from cross-summary.
  // While cross-summary is still loading, show "…" rather than a "—" that reads as broken.
  const ph = crossLoading ? "…" : "—";
  const money = (v) => (v == null ? ph : formatCurrency(v, { dp: 0 }));
  const crossLinks = [
    { to: "/data", tab: "Data", color: "#34d399", headline: stats ? `${formatNumber(stats.totalPriceFiles)} files` : "—", sub: stats ? `${stats.suppliersCovered} suppliers with price files` : "price files uploaded" },
    { to: "/reports", tab: "Reports", color: "#fbbf24", headline: cross?.no_price_file_lines != null ? `${formatNumber(cross.no_price_file_lines)} lines` : ph, sub: "invoice lines with no price file" },
    { to: "/internal-price-comparison", tab: "Internal Prices", color: "#fb7185", headline: cross?.internal_disagreements != null ? `${formatNumber(cross.internal_disagreements)} products` : ph, sub: cross?.internal_max_spread != null ? `systems disagree, up to ${formatCurrency(cross.internal_max_spread, { dp: 0 })} spread` : "systems disagree" },
    { to: "/inflation-index", tab: "Inflation", color: (cross?.inflation_composite_pct ?? 0) >= 0 ? "#fb7185" : "#34d399", headline: cross?.inflation_composite_pct != null ? `${cross.inflation_composite_pct > 0 ? "+" : ""}${cross.inflation_composite_pct}%` : ph, sub: "composite price move, last 12 months" },
    { to: "/benchmark", tab: "Benchmark", color: "#34d399", headline: money(cross?.benchmark_saving), sub: cross?.benchmark_products != null ? `potential annual saving, ${formatNumber(cross.benchmark_products)} products` : "potential annual saving" },
  ];

  // Supplier metrics, optionally filtered to selected months. With no months
  // selected we show the all-time rows; with months selected we sum each
  // supplier's per-month figures (one row per supplier).
  const displayedMetrics = (() => {
    if (selectedMonths.size === 0) return metrics || [];
    const rows = (metricsByMonth || []).filter((m) => selectedMonths.has(m.month));
    if (selectedMonths.size === 1) return rows;
    const bySupplier = new Map();
    for (const r of rows) {
      const a = bySupplier.get(r.supplier) || {
        supplier: r.supplier, covered_months: 0, invoice_months: 0, window_months: 0,
        total_products: 0, matched_products: 0, total_spend: 0, matched_spend: 0,
        overcharge_value: 0, net_overcharge: 0, credit_received: 0,
      };
      a.covered_months += r.covered_months ?? 0;
      a.invoice_months += r.invoice_months ?? 0;
      a.window_months += r.window_months ?? 0;
      a.total_products += r.total_products ?? 0;
      a.matched_products += r.matched_products ?? 0;
      a.total_spend += r.total_spend ?? 0;
      a.matched_spend += r.matched_spend ?? 0;
      a.overcharge_value += r.overcharge_value ?? 0;
      a.net_overcharge += r.net_overcharge ?? r.overcharge_value ?? 0;
      a.credit_received += r.credit_received ?? 0;
      bySupplier.set(r.supplier, a);
    }
    return [...bySupplier.values()];
  })();

  const metricRows = displayedMetrics.map((m) => {
    const cov = pct(m.covered_months, m.window_months ?? m.invoice_months);
    const priced = pct(m.matched_products, m.total_products);
    const spend = pct(m.matched_spend, m.total_spend);
    return {
      supplier: m.supplier, cov, priced, spend,
      overcharge: m.overcharge_value || 0,
      net: m.net_overcharge ?? m.overcharge_value ?? 0,
      credit: m.credit_received || 0,
    };
  });
  const anyCredit = metricRows.some((r) => r.credit > 0);

  const toggleMonth = (mo) => setSelectedMonths((prev) => {
    const next = new Set(prev);
    next.has(mo) ? next.delete(mo) : next.add(mo);
    return next;
  });

  const supplierColumns = [
    { key: "supplier", label: "Supplier", width: "1.4fr", render: (r) => (
      <button onClick={() => setSelectedSupplier(r.supplier)} style={{ background: "none", border: "none", padding: 0, font: "inherit", color: "var(--color-text-1)", fontWeight: 600, cursor: "pointer", textAlign: "left" }}>{r.supplier}</button>
    ) },
    { key: "cov", label: "Price File Coverage", width: "1fr", render: (r) => <StatusBadge value={r.cov} good={95} ok={75} /> },
    { key: "priced", label: "Products Priced", width: "1fr", render: (r) => <StatusBadge value={r.priced} good={60} ok={35} /> },
    { key: "spend", label: "Spend Covered", width: "1.2fr", render: (r) => <StatusBadge value={r.spend} good={90} ok={70} /> },
    { key: "overcharge", label: anyCredit ? "Overcharge (gross)" : "Overcharge Value", width: "1fr", align: "right", numeric: true,
      render: (r) => <span style={{ fontWeight: 700, color: r.overcharge > 0 ? "#fb7185" : "#786a97" }}>{formatCurrency(r.overcharge)}</span> },
    ...(anyCredit ? [
      { key: "credit", label: "Recovered", width: "0.9fr", align: "right", numeric: true,
        render: (r) => <span style={{ fontWeight: 600, color: r.credit > 0 ? "#34d399" : "#786a97" }}>{r.credit > 0 ? formatCurrency(r.credit) : "—"}</span> },
      { key: "net", label: "Net Overcharge", width: "1fr", align: "right", numeric: true,
        render: (r) => <span style={{ fontWeight: 700, color: r.net > 0 ? "#fb7185" : "#34d399" }}>{formatCurrency(r.net)}</span> },
    ] : []),
  ];

  // "Needs attention" strip: actionable counts built from data already loaded,
  // so it adds no extra request. Only shown when there is something to action.
  const staleFiles = (freshness?.suppliers || []).filter((s) => (s.monthsStale ?? 0) >= 3);
  const uncaptured = (reductions?.reductions || []).length;
  const attention = [];
  if (uncaptured > 0)
    attention.push({ to: "/reports", label: `${formatNumber(uncaptured)} uncaptured price reduction${uncaptured === 1 ? "" : "s"}` });
  if (staleFiles.length > 0)
    attention.push({ to: "/data", label: `${formatNumber(staleFiles.length)} price file${staleFiles.length === 1 ? "" : "s"} 3+ months old` });
  if (diCount > 0)
    attention.push({ to: "/reports", label: `${formatNumber(diCount)} line${diCount === 1 ? "" : "s"} flagged as likely data issues` });

  if (!stats || !over) return <div data-testid="dashboard-page"><PageHeader title="Dashboard" /><p role="status" data-testid="dashboard-loading">Loading verified dashboard figures…</p></div>;

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        title={`${greetingPart()}, ${firstNameOf(user)}`}
        subtitle="Overview of invoice and price-file matching · transport/freight exclusion configurable in Settings"
      />

      {attention.length > 0 && (
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 20 }}>
          <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--color-text-4)" }}>Needs attention</span>
          {attention.map((a, i) => (
            <Link key={i} to={a.to} style={{ textDecoration: "none" }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "7px 13px", borderRadius: 999, background: "rgba(251,191,36,0.1)", border: "1px solid rgba(251,191,36,0.3)", fontSize: 13, fontWeight: 600, color: "#fcd34d" }}>
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#fbbf24", display: "inline-block" }} />
                {a.label}
              </span>
            </Link>
          ))}
        </div>
      )}

      {threshold > 0 && (
        <div style={{ display: "flex", alignItems: "center", gap: 10, background: "rgba(251,191,36,0.09)", border: "1px solid rgba(251,191,36,0.3)", borderRadius: 12, padding: "14px 18px", marginBottom: 24, fontSize: 14.5, color: "#fcd34d" }}>
          <span>⚠</span>
          <span>Overcharge threshold <b>{threshold.toFixed(2)}%</b> variance applied. <Link to="/settings" style={{ color: "#fcd34d", textDecoration: "underline" }}>Change in Settings</Link></span>
        </div>
      )}

      <KpiRow items={kpis} cols={3} />

      {/* Hero — Total Potential Credit Value */}
      <Card accent="rgba(236,72,153,0.25)" style={{ marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 14, flexWrap: "wrap" }}>
          <div style={{ fontSize: 13, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--color-text-4)" }}>
            {hasCredits ? "Net Outstanding Credit" : "Total Potential Credit Value"}
          </div>
          <Badge tone="green">TRUE OVERCHARGE</Badge>
          {stats?.overRecovered && <Badge tone="amber">OVER-RECOVERED</Badge>}
        </div>
        <div style={{ fontFamily: "var(--font-display)", fontSize: 42, fontWeight: 800, marginTop: 6 }}>
          {formatCurrency(hasCredits ? netValue : creditValue)}
        </div>
        <div style={{ fontSize: 13.5, color: "var(--color-text-3)", marginTop: 6 }}>
          {hasCredits
            ? "Still recoverable after credits logged. Likely data errors excluded."
            : "True overcharges vs price files. Likely data errors excluded."}
        </div>
        {hasCredits && (
          <div style={{ marginTop: 12, display: "flex", gap: 10, flexWrap: "wrap", fontSize: 13 }}>
            <span style={{ padding: "7px 12px", borderRadius: 9, background: "rgba(255,255,255,0.04)", border: "1px solid var(--hairline)", color: "var(--color-text-2)" }}>
              <b style={{ color: "var(--color-text-1)" }}>{formatCurrency(identifiedValue)}</b> identified
            </span>
            <span style={{ padding: "7px 12px", borderRadius: 9, background: "rgba(52,211,153,0.1)", border: "1px solid rgba(52,211,153,0.3)", color: "#6ee7b7" }}>
              <b>{formatCurrency(recoveredValue)}</b> recovered (credited)
            </span>
            {expectedValue > 0 && (
              <span style={{ padding: "7px 12px", borderRadius: 9, background: "rgba(103,224,240,0.1)", border: "1px solid rgba(103,224,240,0.3)", color: "#8ee9f5" }}>
                <b>{formatCurrency(expectedValue)}</b> expected (in progress)
              </span>
            )}
            <Link to="/credits" style={{ padding: "7px 12px", borderRadius: 9, fontSize: 13, fontWeight: 600, alignSelf: "center" }}>Manage credits →</Link>
          </div>
        )}
        {diValue > 0 && (
          <div style={{ marginTop: 12, display: "inline-flex", alignItems: "center", gap: 8, padding: "7px 12px", borderRadius: 9, background: "rgba(251,191,36,0.1)", border: "1px solid rgba(251,191,36,0.3)", fontSize: 13, color: "#fcd34d" }}>
            <b>Likely data issues</b>&nbsp;{formatCurrency(diValue)} across {formatNumber(diCount)} lines held back for review
          </div>
        )}
      </Card>

      {/* From across ProcOps */}
      <div style={{ marginBottom: 22 }}>
        <div style={{ fontFamily: "var(--font-display)", fontSize: 18, fontWeight: 700, marginBottom: 14 }}>From across ProcOps</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 180px), 1fr))", gap: 14 }}>
          {crossLinks.map((c) => <CrossLink key={c.tab} {...c} />)}
        </div>
      </div>

      {/* Top alerts + data issues */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 340px), 1fr))", gap: 18, marginBottom: 20, alignItems: "start" }}>
        <Card>
          <SectionHeading
            title="Top Overcharge Alerts"
            subtitle="Highest-value discrepancies to action first"
            right={<Link to="/reports" style={{ fontSize: 13, fontWeight: 600 }}>View report →</Link>}
          />
          {(over?.top_overcharges || []).slice(0, 5).map((o, i) => (
            <AlertRow key={i} rank={i + 1}
              title={o.sku || o.description?.slice(0, 30) || "Unknown"}
              sub={`${o.supplier} · ${o.instance_count ?? 0} instances · avg ${formatCurrency(o.avg_overcharge)}`}
              value={formatCurrency(o.total_overcharge)} valueColor="#fb7185"
              rankTone={{ bg: "rgba(236,72,153,0.15)", color: "#ec4899" }}
              onClick={() => setSelectedSupplier(o.supplier)} />
          ))}
          {(!over || over.top_overcharges.length === 0) && <div style={{ padding: "24px 0", color: "var(--color-text-3)", fontSize: 14 }}>No overcharges detected.</div>}
        </Card>

        <Card>
          <SectionHeading title="Likely Data Issues: Review First" subtitle="Implausible variances, excluded from credit until confirmed" />
          {(over?.data_issues || []).slice(0, 5).map((d, i) => (
            <AlertRow key={i} rank={i + 1}
              title={d.sku || d.description?.slice(0, 30) || "Unknown"}
              sub={`${d.supplier} · invoiced ${Number(d.example_variance_pct ?? 0).toLocaleString("en-GB")}% above price file`}
              value={formatCurrency(d.flagged_value)} valueColor="#fbbf24"
              rankTone={{ bg: "rgba(251,191,36,0.15)", color: "#fbbf24" }}
              onClick={() => setSelectedSupplier(d.supplier)} />
          ))}
          {(!over || over.data_issues.length === 0) && <div style={{ padding: "24px 0", color: "var(--color-text-3)", fontSize: 14 }}>No data issues flagged.</div>}
        </Card>
      </div>

      {/* Trends chart */}
      <Card style={{ marginBottom: 20 }}>
        <SectionHeading title="Overcharges Over Time" subtitle="Composite trend · per-supplier breakdown on the Reports page" />
        <OverchargeTrendsChart />
      </Card>

      {/* Price impact over time — modelled cost impact of approved submissions at point of approval */}
      <SectionHeading title="Price impact over time" style={{ marginTop: 28 }}
        subtitle="Modelled annual cost impact of approved price submissions, captured at the point of approval (BOM / install basis). Up is added cost, down is saving." />
      {impact == null ? (
        <Card><div style={{ color: "var(--color-text-3)" }}>Loading…</div></Card>
      ) : !impact.loaded ? (
        <Card><div style={{ color: "var(--color-text-3)", fontSize: 13.5 }}>Load the platform BOM in Settings (Budget Modeller, Platform BOM) to model submission impact.</div></Card>
      ) : (
        <>
          <KpiRow cols={3} items={[
            { label: "Net annual impact captured", value: formatCurrency(impact.cumulative || 0),
              valueColor: (impact.cumulative || 0) > 0 ? "#fb7185" : (impact.cumulative || 0) < 0 ? "#34d399" : "var(--color-text-1)",
              hint: "Sum of the modelled annual cost change from every approved submission. Positive is added cost, negative is saving." },
            { label: "Submissions modelled", value: (impact.submissions || []).length },
            { label: "Latest effective", value: impact.submissions?.[0]?.effective || "—" },
          ]} />
          {(impact.series || []).length > 0 && (
            <Card style={{ marginBottom: 16 }}>
              <ResponsiveContainer width="100%" height={240}>
                <ComposedChart data={impact.series.map((s) => ({ ...s, label: monthShort(s.month) }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#2a1c47" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#9d8fc0" }} />
                  <YAxis tick={{ fontSize: 11, fill: "#9d8fc0" }} tickFormatter={gbpK} />
                  <Tooltip contentStyle={{ background: "#17102b", border: "1px solid #2a1c47", borderRadius: 8, color: "#f5f2fb", fontSize: 12 }}
                    formatter={(v, n) => [formatCurrency(v), n === "cumulative" ? "Cumulative" : "This month"]} />
                  <Bar dataKey="impact" name="This month" radius={[4, 4, 0, 0]}>
                    {impact.series.map((s, i) => <Cell key={i} fill={s.impact >= 0 ? "#fb7185" : "#34d399"} />)}
                  </Bar>
                  <Line type="monotone" dataKey="cumulative" name="Cumulative" stroke="#a855f7" strokeWidth={2} dot={false} />
                </ComposedChart>
              </ResponsiveContainer>
            </Card>
          )}
          <DataTable
            columns={[
              { key: "supplier", label: "Supplier", width: "1.4fr", render: (r) => <span style={{ fontWeight: 600 }}>{r.supplier || "—"}</span> },
              { key: "effective", label: "Effective", width: "0.9fr", render: (r) => r.effective || "—" },
              { key: "annualDelta", label: "Annual impact", align: "right", width: "1fr", sortValue: (r) => r.annualDelta,
                render: (r) => <span style={{ fontWeight: 600, color: r.annualDelta > 0 ? "#fb7185" : r.annualDelta < 0 ? "#34d399" : "var(--color-text-2)" }}>{formatCurrency(r.annualDelta)}</span> },
              { key: "deltaPct", label: "% / install", align: "right", width: "0.8fr", render: (r) => r.deltaPct == null ? "—" : `${r.deltaPct > 0 ? "+" : ""}${r.deltaPct}%` },
              { key: "matched", label: "Matched", align: "right", width: "0.8fr", render: (r) => `${r.matched} / ${r.priced}` },
              { key: "uomWarnings", label: "UoM?", align: "right", width: "0.6fr", render: (r) => r.uomWarnings > 0 ? <span style={{ color: "#fbbf24", fontWeight: 700 }}>{r.uomWarnings}</span> : "—" },
            ]}
            rows={impact.submissions || []}
            perPage={10}
            defaultSortKey="effective"
            defaultSortDir="desc"
            searchKeys={["supplier"]}
            searchPlaceholder="Search supplier…"
            emptyText="No approved submissions modelled yet."
          />
        </>
      )}

      {/* Uncaptured price reductions — invoiced below the price file, never submitted */}
      <SectionHeading
        title="Uncaptured price reductions"
        subtitle="Invoiced below the price file but never submitted"
        style={{ marginTop: 28 }}
        right={
          <button
            onClick={exportReductions}
            disabled={!(reductions?.reductions || []).length}
            style={{ fontSize: 13, fontWeight: 600, color: "var(--color-primary-500)", background: "transparent", border: "1px solid var(--hairline)", borderRadius: 8, padding: "5px 12px", cursor: (reductions?.reductions || []).length ? "pointer" : "not-allowed", opacity: (reductions?.reductions || []).length ? 1 : 0.5 }}
          >
            Export CSV
          </button>
        }
      />
      <DataTable
        columns={[
          { key: "supplier", label: "Supplier", width: "1.2fr" },
          { key: "sku", label: "SKU", width: "0.9fr",
            render: (r) => <span title={r.description} style={{ fontWeight: 600 }}>{r.sku || r.description?.slice(0, 20) || "—"}</span> },
          { key: "priceFilePrice", label: "Price file", align: "right", width: "0.8fr", render: (r) => formatCurrency(r.priceFilePrice) },
          { key: "observedPrice", label: "Invoiced", align: "right", width: "0.8fr", render: (r) => <span style={{ color: "#34d399", fontWeight: 600 }}>{formatCurrency(r.observedPrice)}</span> },
          { key: "pctBelow", label: "% below", align: "right", width: "0.7fr", render: (r) => `${r.pctBelow}%` },
          { key: "invoices", label: "Invoices", align: "right", width: "0.7fr", render: (r) => `${r.invoices} / ${r.months}mo` },
          { key: "confidence", label: "Confidence", align: "right", width: "0.9fr",
            render: (r) => <span style={{ fontWeight: 700, color: r.band === "High" ? "#34d399" : r.band === "Medium" ? "#fbbf24" : "var(--color-text-3)" }}>{r.band}</span> },
          { key: "estAnnualValue", label: "Est. / yr", align: "right", width: "0.9fr", render: (r) => formatCurrency(r.estAnnualValue) },
          { key: "action", label: "", align: "right", width: "1.1fr", sortable: false,
            render: (r) => (
              <span style={{ display: "inline-flex", gap: 6 }}>
                <Link to={`/portal-admin?supplier=${encodeURIComponent(r.supplier)}&sku=${encodeURIComponent(r.sku || "")}&price=${r.observedPrice}`}
                  style={{ fontSize: 12, fontWeight: 600, color: "var(--color-primary-500)", textDecoration: "none", border: "1px solid var(--hairline)", borderRadius: 7, padding: "3px 9px" }}>Submit</Link>
                <button onClick={() => dismissReduction(r.supplier, r.sku)}
                  style={{ fontSize: 12, fontWeight: 600, color: "var(--color-text-3)", background: "transparent", border: "1px solid var(--hairline)", borderRadius: 7, padding: "3px 9px", cursor: "pointer" }}>Dismiss</button>
              </span>
            ) },
        ]}
        rows={reductions?.reductions || []}
        perPage={10}
        searchKeys={["supplier", "sku", "description"]}
        searchPlaceholder="Search supplier or SKU..."
        emptyText="No uncaptured reductions detected. When a supplier invoices below the price file for a while, it shows here so the new price can be submitted."
      />

      {/* Price file freshness — most recent price file on record per supplier */}
      <SectionHeading title="Price file freshness" subtitle="Most recent price file held per supplier, and how old it is" style={{ marginTop: 28 }} />
      <DataTable
        columns={[
          { key: "supplier", label: "Supplier", width: "1.5fr", render: (r) => <span style={{ fontWeight: 600 }}>{r.supplier}</span> },
          { key: "latestMonth", label: "Latest file", width: "0.9fr", render: (r) => r.latestMonth || "—" },
          { key: "monthsStale", label: "Freshness", align: "right", width: "1fr", sortValue: (r) => (r.monthsStale == null ? 9999 : r.monthsStale),
            render: (r) => {
              const m = r.monthsStale;
              if (m == null) return "—";
              if (m > 0) return <span style={{ fontWeight: 700, color: m <= 2 ? "#fbbf24" : "#fb7185" }}>{m}mo old</span>;
              if (m === 0) return <span style={{ fontWeight: 700, color: "#34d399" }}>current</span>;
              return <span style={{ fontWeight: 700, color: "#34d399" }}>valid {Math.abs(m)}mo more</span>;
            } },
          { key: "itemCount", label: "SKUs", align: "right", width: "0.6fr", render: (r) => (r.itemCount || 0).toLocaleString() },
          { key: "uploadedAt", label: "Uploaded", align: "right", width: "1fr", sortValue: (r) => r.uploadedAt || "",
            render: (r) => r.uploadedAt ? new Date(r.uploadedAt).toLocaleDateString("en-GB") : "—" },
          { key: "fileCount", label: "Files", align: "right", width: "0.6fr", render: (r) => r.fileCount },
        ]}
        rows={freshness?.suppliers || []}
        perPage={10}
        defaultSortKey="monthsStale"
        defaultSortDir="desc"
        searchKeys={["supplier"]}
        searchPlaceholder="Search supplier…"
        emptyText="No price files on record yet."
      />

      {/* Supplier metrics — sortable + month-filterable */}
      <DataTable
        title="Supplier Metrics"
        columns={supplierColumns}
        rows={metricRows}
        perPage={10}
        searchKeys={["supplier"]}
        searchPlaceholder="Search supplier..."
        emptyText="No supplier metrics yet."
        right={
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            {[...selectedMonths].sort().map((mo) => (
              <span key={mo} onClick={() => toggleMonth(mo)}
                style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "5px 10px", borderRadius: 999, fontSize: 12, fontWeight: 600, cursor: "pointer", background: "var(--color-primary-16)", color: "var(--color-primary-500)" }}>
                {mo} ✕
              </span>
            ))}
            {availableMonths.length > 0 && (
              <select
                value=""
                onChange={(e) => { if (e.target.value) toggleMonth(e.target.value); }}
                style={{ background: "var(--color-bg-panel)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 9, padding: "8px 10px", color: "var(--color-text-2)", fontSize: 13, fontFamily: "var(--font-family)" }}
              >
                <option value="">{selectedMonths.size ? "+ Add month" : "Filter by month"}</option>
                {availableMonths.filter((m) => !selectedMonths.has(m)).map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            )}
          </div>
        }
      />

      <SupplierOverchargeModal supplier={selectedSupplier} open={!!selectedSupplier} onClose={() => setSelectedSupplier(null)} />
    </div>
  );
};

export default Dashboard;
